import { VPadStage } from "@/interfaces/virtualPad_types"
import { VPadP2PAction, VPadP2PActionType } from "../p2p_actions"
import { VPAD_AXIS_TO_GAMEPAD_AXES, VPAD_KEY_CODE_TO_BUTTON_INDEX } from "../p2p_mapping"
import { VPadP2PProtocol } from "../p2p_protocol"
import { VPadP2PAxis, VPadP2PButtonMsg, VPadP2PCloseMsg, VPadP2PCommunicationMsg, VPadP2PCommunicationSubtype, VPadP2PEjectMsg, VPadP2PIncomingMsg, VPadP2PInputMsg, VPadP2PInputSubtype, VPadP2PKeyCodes, VPadP2PLeaveMsg, VPadP2PMovementMsg, VPadP2POutgoingMsg, VPadP2PPingMsg, VPadP2PPongMsg, VPadP2PStatusMsg, VPadP2PTypes } from "../p2p_types"

/**
 * Implements version 1 of the virtual pad P2P protocol.
 *
 * It parses incoming DataChannel messages, converts them into
 * internal virtual pad actions and serializes outgoing messages.
 */
export class VPadP2PProtocolV1 implements VPadP2PProtocol {
  /**
   * Parses and validates raw data received through the DataChannel
   * 
   * Version 1 of the protocol only supports JSON messages encoded as strings
   * The parsed message must match one of the incoming message structures supported
   * by this protocol version.
   * 
   * @param data Raw data received through the DataChannel
   * @returns Parsed and validated incoming P2P message
   * @throws If the data is not a string, valid JSON or a supported message
   */
  public parse(data: unknown): VPadP2PIncomingMsg {
    if (typeof data !== 'string')
      throw new Error('VPAD P2P PROTOCOL V1 :: Received data must be a string')

    let parsed: unknown

    try {
      parsed = JSON.parse(data)
    } catch {
      throw new Error('VPAD P2P PROTOCOL V1 :: Received data is not valid JSON')
    }

    return this.validateMessage(parsed)
  }

  /**
   * Validates a parsed value as an incoming P2P message.
   *
   * @param message Parsed JSON value
   * @returns Validated incoming P2P message
   * @throws If the value does not represent a supported message
   */
  private validateMessage(message: unknown): VPadP2PIncomingMsg {
    if (!this.isRecord(message))
      throw new Error('VPAD P2P PROTOCOL V1 :: VALIDATE MESSAGE :: Message must be an object')

    switch (message.type) {
      case VPadP2PTypes.INPUT:
        return this.validateInputMessage(message)

      case VPadP2PTypes.COMMUNICATION:
        return this.validateCommunicationMessage(message)

      default:
        throw new Error(`VPAD P2P PROTOCOL V1 :: VALIDATE MESSAGE :: Unsupported message type: ${String(message.type)}`)
    }
  }

  /**
   * Validates an incoming input message.
   *
   * @param message Parsed input message
   * @returns Validated button or movement message
   * @throws If the input subtype or data is invalid
   */
  private validateInputMessage(message: Record<string, unknown>): VPadP2PInputMsg {
    switch (message.subtype) {
      case VPadP2PInputSubtype.BUTTON:
        return this.validateButtonMessage(message)

      case VPadP2PInputSubtype.MOVEMENT:
        return this.validateMovementMessage(message)

      default:
        throw new Error(`VPAD P2P PROTOCOL V1 :: VALIDATE INPUT MESSAGE :: Unsupported input subtype: ${String(message.subtype)}`)
    }
  }

  /**
   * Validates an incoming communication message.
   *
   * @param message Parsed communication message
   * @returns Validated close message
   * @throws If the communication subtype is not supported
   */
  private validateCommunicationMessage(message: Record<string, unknown>): VPadP2PCommunicationMsg {
    switch (message.subtype) {
      case VPadP2PCommunicationSubtype.CLOSE:
        return this.validateCloseMessage(message)

      case VPadP2PCommunicationSubtype.PING:
        return this.validatePingMessage(message)

      case VPadP2PCommunicationSubtype.STATUS:
        return this.validateStatusMessage(message)

      default:
        throw new Error(`VPAD P2P PROTOCOL V1 :: VALIDATE COMMUNICATION MESSAGE :: Unsupported communication subtype: ${String(message.subtype)}`)
    }
  }

  /**
   * Validates an incoming button message.
   *
   * @param message Parsed button message
   * @returns Validated button message
   * @throws If the button data is invalid
   */
  private validateButtonMessage(message: Record<string, unknown>): VPadP2PButtonMsg {
    const data = message.data

    if (!this.isRecord(data))
      throw new Error('VPAD P2P PROTOCOL V1 :: VALIDATE BUTTON MESSAGE :: Button message data must be an object')

    const { keyCode, value } = data

    if (!this.isKeyCode(keyCode))
      throw new Error(`VPAD P2P PROTOCOL V1 :: VALIDATE BUTTON MESSAGE :: Unsupported button key code: ${String(keyCode)}`)

    if (
      typeof value !== 'number' ||
      !Number.isFinite(value) ||
      value < 0 ||
      value > 1
    ) {
      throw new Error('VPAD P2P PROTOCOL V1 :: VALIDATE BUTTON MESSAGE :: Button value must be a finite number between 0 and 1')
    }

    return message as unknown as VPadP2PButtonMsg
  }

  /**
   * Validates an incoming joystick movement message.
   *
   * @param message Parsed movement message
   * @returns Validated movement message
   * @throws If the movement data is invalid
   */
  private validateMovementMessage(message: Record<string, unknown>): VPadP2PMovementMsg {
    const data = message.data

    if (!this.isRecord(data))
      throw new Error('VPAD P2P PROTOCOL V1 :: VALIDATE MOVEMENT MESSAGE :: Movement message data must be an object')

    const { axis, values } = data

    if (!this.isAxis(axis))
      throw new Error(`VPAD P2P PROTOCOL V1 :: VALIDATE MOVEMENT MESSAGE :: Unsupported joystick axis: ${String(axis)}`)

    if (!Array.isArray(values) || values.length !== 2) {
      throw new Error('VPAD P2P PROTOCOL V1 :: VALIDATE MOVEMENT MESSAGE :: Movement values must contain exactly two values')
    }

    const [horizontalValue, verticalValue] = values

    if (
      !this.isValidAxisValue(horizontalValue) ||
      !this.isValidAxisValue(verticalValue)
    ) {
      throw new Error('VPAD P2P PROTOCOL V1 :: VALIDATE MOVEMENT MESSAGE :: Movement values must be finite numbers between -1 and 1')
    }

    return message as unknown as VPadP2PMovementMsg
  }

  /**
   * Validates an outgoing close message
   * 
   * @param message Parsed close message
   * @returns Validated close message
   */
  private validateCloseMessage(message: Record<string, unknown>): VPadP2PCloseMsg {
    return message as unknown as VPadP2PCloseMsg
  }

  /**
   * Validates an incoming ping message
   */
  private validatePingMessage(message: Record<string, unknown>): VPadP2PPingMsg {
    if (
      typeof message.sentAt !== 'string' ||
      message.sentAt.trim() === ''
    ) {
      throw new Error(
        'VPAD P2P PROTOCOL V1 :: VALIDATE PING MESSAGE :: sentAt must be a non-empty string'
      )
    }

    return message as unknown as VPadP2PPingMsg
  }

  /**
   * Validates an incoming status message
   */
  private validateStatusMessage(message: Record<string, unknown>): VPadP2PStatusMsg {
    const data = message.data

    if (!this.isRecord(data))
      throw new Error('VPAD P2P PROTOCOL V1 :: VALIDATE STATUS MESSAGE :: Status message data must be an object')

    if (
      data.status !== VPadStage.CONNECTED &&
      data.status !== VPadStage.SUSPENDED
    ) {
      throw new Error(
        `VPAD P2P PROTOCOL V1 :: VALIDATE STATUS MESSAGE :: Unsupported status: ${String(data.status)}`
      )
    }

    return message as unknown as VPadP2PStatusMsg
  }

  /**
   * Serializes an outgoing P2P message before sending it through
   * the DataChannel
   * 
   * @param message Outgoing P2P message
   * @returns JSON string representation of the message
   */
  public encode(message: VPadP2POutgoingMsg): string {
    return JSON.stringify(message)
  }

  /**
   * Creates a message indicating that the host has manually removed the remote virtual pad
   * 
   * @returns Leave communication message
   */
  public createLeaveMessage(): VPadP2PLeaveMsg {
    return {
      type: VPadP2PTypes.COMMUNICATION,
      subtype: VPadP2PCommunicationSubtype.LEAVE
    }
  }

  /**
   * Creates a message indicating that the host has automatically ejected
   * the remote virtual pad to free a controller slot
   *
   * @returns Eject communication message
   */
  public createEjectMessage(): VPadP2PEjectMsg {
    return {
      type: VPadP2PTypes.COMMUNICATION,
      subtype: VPadP2PCommunicationSubtype.EJECT
    }
  }

  /**
   * Creates a response to a ping received from the remote virtual pad
   */
  public createPongMessage(sentAt: string): VPadP2PPongMsg {
    return {
      type: VPadP2PTypes.COMMUNICATION,
      subtype: VPadP2PCommunicationSubtype.PONG,
      sentAt
    }
  }

  /**
   * Converts an incoming P2P protocol message into an internal virtual pad action
   * 
   * @param message Parsed incoming P2P message
   * @returns Action understood by the virtual pad connection
   */
  public getAction(message: VPadP2PIncomingMsg): VPadP2PAction {
    switch (message.type) {
      case VPadP2PTypes.INPUT:
        return this.getInputAction(message)

      case VPadP2PTypes.COMMUNICATION:
        return this.getCommunicationAction(message)
    }
  } 

  /**
   * Converts an input protocol message into a button or movement action
   * 
   * Protocol-specific button and axis identifiers are mapped to their
   * corresponding standard Gamepad API indexes
   * 
   * @param message Incoming P2P input message
   * @returns Button or movement action
   */
  private getInputAction(message: VPadP2PInputMsg): VPadP2PAction {
    switch (message.subtype) {
      case VPadP2PInputSubtype.BUTTON:
        return {
          type: VPadP2PActionType.BUTTON,
          buttonIndex: VPAD_KEY_CODE_TO_BUTTON_INDEX[message.data.keyCode],
          value: message.data.value,
        }

      case VPadP2PInputSubtype.MOVEMENT: {
        const [horizontalIndex, verticalIndex] = VPAD_AXIS_TO_GAMEPAD_AXES[message.data.axis]
        const [horizontalValue, verticalValue] = message.data.values

        return {
          type: VPadP2PActionType.MOVEMENT,
          axes: [
            {
              index: horizontalIndex,
              value: horizontalValue,
            },
            {
              index: verticalIndex,
              value: verticalValue,
            },
          ],
        }
      }

      default:
        throw new Error(`VPAD P2P PROTOCOL V1 :: GET INPUT ACTION :: Unsupported message: ${JSON.stringify(message)}`)
    }
  }

  /**
   * Converts an incoming communication message into a connection action
   * 
   * @param message Incoming P2P communication message
   * @returns A communication action
   */
  private getCommunicationAction(message: VPadP2PCommunicationMsg): VPadP2PAction {
    switch (message.subtype) {
      case VPadP2PCommunicationSubtype.CLOSE:
        return {
          type: VPadP2PActionType.CLOSE,
        }

      case VPadP2PCommunicationSubtype.PING:
        return {
          type: VPadP2PActionType.PING,
          sentAt: message.sentAt
        }

      case VPadP2PCommunicationSubtype.STATUS:
        return {
          type: VPadP2PActionType.STATUS,
          status: message.data.status
        }
 
      default:
        throw new Error(`VPAD P2P PROTOCOL V1 :: GET COMMUNICATION ACTION :: Unsupported message: ${JSON.stringify(message)}`)
    }
  }

  /**
   * Checks whether a value is a non-null object
   */
  private isRecord(value: unknown): value is Record<string, unknown> {
    return (
      typeof value === 'object' &&
      value !== null &&
      !Array.isArray(value)
    )
  }

  /**
   * Checks whether a value is a supported virtual pad key code
   */
  private isKeyCode(value: unknown): value is VPadP2PKeyCodes {
    return (
      typeof value === 'string' &&
      Object.values(VPadP2PKeyCodes).includes(
        value as VPadP2PKeyCodes
      )
    )
  }

  /**
   * Checks whether a value is a supported joystick identifier
   */
  private isAxis(value: unknown): value is VPadP2PAxis {
    return (
      typeof value === 'string' &&
      Object.values(VPadP2PAxis).includes(
        value as VPadP2PAxis
      )
    )
  }

  /**
   * Checks whether a value is a valid Gamepad API axis value
   */
  private isValidAxisValue(value: unknown): value is number {
    return (
      typeof value === 'number' &&
      Number.isFinite(value) &&
      value >= -1 &&
      value <= 1
    )
  }
}